import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Mail,
  Phone,
  Building2,
  Briefcase,
  User,
  Clock,
  CheckCircle2,
  Info,
  Loader2,
  RotateCcw,
  MessageSquare,
  Calendar,
  Copy,
  ChevronLeft,
  ChevronRight,
  AlertTriangle,
  X,
} from 'lucide-react';
import { EmployeeImage } from '@/components/EmployeeImage';
import { WorkingPatternForm } from '@/components/WorkingPatternForm';
import { WorkflowAssignmentCard } from '@/components/WorkflowAssignmentCard';
import { WorkflowSelectionModal } from '@/components/WorkflowSelectionModal';
import type { Employee, WorkingPattern } from '@/types/timesheet';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { useScreenDataMode } from '@/hooks/useScreenDataMode';
import { useWorkflowAssignment } from '@/hooks/useWorkflowAssignment';
import { ApiError } from '@/services/apiClient';
import * as settingsRepository from '@/services/settingsRepository';

interface TeamMember extends Employee {
  status: 'active' | 'inactive' | 'on-leave';
  phone?: string;
  location?: string;
  startDate?: Date;
}

interface TeamMemberDetailsDialogProps {
  member: TeamMember | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onMemberUpdate?: (member: TeamMember) => void;
  getStatusColor: (status: 'active' | 'inactive' | 'on-leave') => string;
  formatDate: (date: Date) => string;
}

export const TeamMemberDetailsDialog: React.FC<TeamMemberDetailsDialogProps> = ({
  member,
  open,
  onOpenChange,
  onMemberUpdate,
  getStatusColor,
  formatDate,
}) => {
  const { mode } = useScreenDataMode('team-members');
  
  const [workingPattern, setWorkingPattern] = useState<WorkingPattern | null>(null);
  const [isInherited, setIsInherited] = useState(true);
  const [patternId, setPatternId] = useState<string | undefined>(undefined);
  
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showSaveSuccess, setShowSaveSuccess] = useState(false);
  const [showRevertDialog, setShowRevertDialog] = useState(false);
  const [activeTab, setActiveTab] = useState('details');
  const [showWorkflowModal, setShowWorkflowModal] = useState(false);

  // Workflow assignment hook
  const {
    assignment: workflowAssignment,
    isLoading: isLoadingWorkflow,
    assignWorkflow,
    revertToDefault: revertWorkflowToDefault,
  } = useWorkflowAssignment(member?.id || null);

  useEffect(() => {
    if (member && open) {
      loadWorkingPattern();
      setActiveTab('details'); // Reset to details tab when dialog opens
    }
  }, [member, open, mode]);

  const loadWorkingPattern = async () => {
    if (!member) return;
    
    setIsLoading(true);
    
    try {
      const result = await settingsRepository.getEmployeeWorkingPattern(member.id, mode);
      setWorkingPattern(result.pattern);
      setIsInherited(result.inherited);
      setPatternId(result.id);
    } catch (err) {
      console.error('Error loading working pattern:', err);
      toast.error('Failed to load working pattern', {
        description: err instanceof ApiError ? err.message : 'Please try again',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSavePattern = async (pattern: WorkingPattern) => {
    if (!member) return;
    
    setIsSaving(true);
    
    try {
      const result = await settingsRepository.setEmployeeWorkingPattern(member.id, pattern, mode);
      setWorkingPattern(result.pattern);
      setIsInherited(result.inherited);
      setPatternId(result.id);
      setShowSaveSuccess(true);
      setTimeout(() => setShowSaveSuccess(false), 3000);
      
      toast.success('Working pattern saved', {
        description: `${member.name}'s pattern set to ${pattern.hoursPerWeek} hours per week`,
      });
      
      // Switch to details tab to show the updated summary
      setActiveTab('details');
      
      // Update the parent component's state with the new working pattern
      if (onMemberUpdate) {
        onMemberUpdate({
          ...member,
          workingPattern: result.pattern,
        });
      }
      
      console.log('Saved working pattern for', member.name, pattern);
    } catch (err) {
      console.error('Error saving working pattern:', err);
      toast.error('Failed to save working pattern', {
        description: err instanceof ApiError ? err.message : 'Please try again',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleRevertToDefault = async () => {
    if (!member) return;
    
    setIsSaving(true);
    
    try {
      await settingsRepository.deleteEmployeeWorkingPattern(member.id, patternId, mode);
      
      const result = await settingsRepository.getEmployeeWorkingPattern(member.id, mode);
      setWorkingPattern(result.pattern);
      setIsInherited(result.inherited);
      setPatternId(result.id);
      
      toast.success('Reverted to organization default', {
        description: `${member.name} now uses the organization default working pattern`,
      });
      
      // Update the parent component's state with the default working pattern
      if (onMemberUpdate) {
        onMemberUpdate({
          ...member,
          workingPattern: result.pattern,
        });
      }
      
      console.log('Reverted to default for', member.name);
    } catch (err) {
      console.error('Error reverting to default:', err);
      toast.error('Failed to revert to default', {
        description: err instanceof ApiError ? err.message : 'Please try again',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCopyEmail = () => {
    if (member?.email) {
      navigator.clipboard.writeText(member.email);
      toast.success('Email copied to clipboard');
    }
  };

  const handleCopyPhone = () => {
    if (member?.phone) {
      navigator.clipboard.writeText(member.phone);
      toast.success('Phone number copied to clipboard');
    }
  };

  const getWorkingPatternSummary = () => {
    if (!workingPattern) return null;
    
    const activeDays = Object.entries(workingPattern.workingDays)
      .filter(([_, isWorking]) => isWorking)
      .map(([day]) => day.charAt(0).toUpperCase() + day.slice(1, 3));
    
    return {
      hoursPerWeek: workingPattern.hoursPerWeek,
      daysWorking: activeDays.length,
      schedule: activeDays.join(', '),
      isInherited,
    };
  };

  if (!member) return null;

  const workingPatternSummary = getWorkingPatternSummary();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader className="sr-only">
          <DialogTitle>Team Member Details</DialogTitle>
          <DialogDescription>
            View and manage team member information and settings
          </DialogDescription>
        </DialogHeader>

        {/* Success Message */}
        {showSaveSuccess && (
          <Alert className="border-success-500 bg-success-500/10">
            <CheckCircle2 className="h-4 w-4 text-success-600" />
            <AlertDescription className="text-success-600">
              Working pattern saved successfully
            </AlertDescription>
          </Alert>
        )}

        {/* Enhanced Header with Contact Info */}
        <div className="flex flex-col sm:flex-row items-start gap-6 pb-6 border-b">
          <div className="relative flex-shrink-0">
            <EmployeeImage
              name={member.name}
              photoUrl={member.photoUrl}
              size="xl"
            />
            {member.status === 'active' && (
              <div className="absolute -bottom-0.5 -right-0.5 w-4 h-4 bg-success-500 rounded-full border-2 border-background" />
            )}
          </div>
          
          <div className="flex-1 min-w-0 space-y-3">
            {/* Name and Status */}
            <div>
              <h2 className="text-2xl font-bold tracking-tight">{member.name}</h2>
              <p className="text-base text-muted-foreground mt-1">{member.role}</p>
              <div className="flex items-center gap-2 mt-2">
                <Badge
                  variant="outline"
                  className={cn("font-medium", getStatusColor(member.status))}
                >
                  {member.status}
                </Badge>
                {member.department && (
                  <Badge variant="secondary" className="font-normal">
                    {member.department}
                  </Badge>
                )}
              </div>
            </div>

            {/* Contact Information - Prominent */}
            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              <Button
                variant="outline"
                size="sm"
                className="justify-start"
                asChild
              >
                <a href={`mailto:${member.email}`} className="flex items-center gap-2">
                  <Mail className="h-4 w-4" />
                  <span className="truncate">{member.email}</span>
                  <Copy 
                    className="h-3 w-3 ml-auto opacity-0 group-hover:opacity-100 transition-opacity" 
                    onClick={(e) => {
                      e.preventDefault();
                      handleCopyEmail();
                    }}
                  />
                </a>
              </Button>
              
              {member.phone && (
                <Button
                  variant="outline"
                  size="sm"
                  className="justify-start"
                  asChild
                >
                  <a href={`tel:${member.phone}`} className="flex items-center gap-2">
                    <Phone className="h-4 w-4" />
                    <span>{member.phone}</span>
                    <Copy 
                      className="h-3 w-3 ml-auto opacity-0 group-hover:opacity-100 transition-opacity" 
                      onClick={(e) => {
                        e.preventDefault();
                        handleCopyPhone();
                      }}
                    />
                  </a>
                </Button>
              )}
            </div>

            {/* Quick Actions */}
            <div className="flex flex-wrap gap-2 pt-2">
              <Button
                variant="default"
                size="sm"
                asChild
              >
                <a href={`mailto:${member.email}`}>
                  <MessageSquare className="h-4 w-4 mr-2" />
                  Send Email
                </a>
              </Button>
              
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  toast.info('Calendar integration coming soon');
                }}
              >
                <Calendar className="h-4 w-4 mr-2" />
                Schedule Meeting
              </Button>
            </div>
          </div>
        </div>

        {/* Tabbed Content */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="details">
              <User className="h-4 w-4 mr-2" />
              Details
            </TabsTrigger>
            <TabsTrigger value="working-pattern">
              <Clock className="h-4 w-4 mr-2" />
              Working Pattern
            </TabsTrigger>
            <TabsTrigger value="timesheet">
              <CheckCircle2 className="h-4 w-4 mr-2" />
              Timesheet
            </TabsTrigger>
          </TabsList>

          {/* Details Tab */}
          <TabsContent value="details" className="space-y-4 mt-4">
            {/* Working Pattern Summary Card - Enhanced */}
            {workingPatternSummary && (
              <Card className="border-info-200 dark:border-info-800 bg-gradient-to-br from-info-50 to-blueviolet-50 dark:from-info-950/30 dark:to-blueviolet-950/30 shadow-sm">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base font-semibold flex items-center gap-2 text-info-900 dark:text-info-100">
                      <div className="p-1.5 rounded-lg bg-info-500/10">
                        <Clock className="h-4 w-4 text-info-600 dark:text-info-400" />
                      </div>
                      Working Pattern
                    </CardTitle>
                    {workingPatternSummary.isInherited ? (
                      <Badge variant="secondary" className="text-xs bg-info-100 dark:bg-info-900/30 text-info-700 dark:text-info-300 border-info-200 dark:border-info-800">
                        Organization Default
                      </Badge>
                    ) : (
                      <Badge variant="default" className="text-xs bg-info-600 dark:bg-info-500 text-white">
                        Custom Pattern
                      </Badge>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  {/* Hours and Days Grid - Compact Summary */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="p-3 rounded-lg bg-white/60 dark:bg-neutral-900/30 border border-info-100 dark:border-info-900">
                      <p className="text-xs text-info-600 dark:text-info-400 font-medium mb-1">Hours per Week</p>
                      <p className="text-2xl font-bold text-info-900 dark:text-info-100">{workingPatternSummary.hoursPerWeek}h</p>
                      <p className="text-xs text-info-600/70 dark:text-info-400/70 mt-1">
                        {(workingPatternSummary.hoursPerWeek / workingPatternSummary.daysWorking).toFixed(1)}h per day
                      </p>
                    </div>
                    <div className="p-3 rounded-lg bg-white/60 dark:bg-neutral-900/30 border border-info-100 dark:border-info-900">
                      <p className="text-xs text-info-600 dark:text-info-400 font-medium mb-1">Working Days</p>
                      <p className="text-2xl font-bold text-info-900 dark:text-info-100">{workingPatternSummary.daysWorking}</p>
                      <p className="text-xs text-info-600/70 dark:text-info-400/70 mt-1">
                        {workingPatternSummary.schedule}
                      </p>
                    </div>
                  </div>

                  {/* Notes if present */}
                  {workingPattern?.notes && (
                    <div className="p-3 rounded-lg bg-white/60 dark:bg-neutral-900/30 border border-info-100 dark:border-info-900">
                      <p className="text-xs text-info-600 dark:text-info-400 font-medium mb-1">Notes</p>
                      <p className="text-sm text-info-900 dark:text-info-100">{workingPattern.notes}</p>
                    </div>
                  )}

                  {/* Quick Action Button */}
                  <Button
                    variant="default"
                    size="sm"
                    className="w-full"
                    onClick={() => setActiveTab('working-pattern')}
                  >
                    <Clock className="h-3.5 w-3.5 mr-2" />
                    Edit Working Pattern
                  </Button>
                </CardContent>
              </Card>
            )}

            <div className="grid gap-4 sm:grid-cols-2">
              {/* Work Information */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium text-muted-foreground">
                    Work Information
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-start gap-3">
                    <Briefcase className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                    <div className="min-w-0 flex-1">
                      <p className="text-xs text-muted-foreground">Role</p>
                      <p className="text-sm font-medium">{member.role}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Building2 className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                    <div className="min-w-0 flex-1">
                      <p className="text-xs text-muted-foreground">Department</p>
                      <p className="text-sm font-medium">{member.department}</p>
                    </div>
                  </div>
                  {member.location && (
                    <div className="flex items-start gap-3">
                      <Building2 className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                      <div className="min-w-0 flex-1">
                        <p className="text-xs text-muted-foreground">Location</p>
                        <p className="text-sm font-medium">{member.location}</p>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Employment Details */}
              {member.startDate && (
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-medium text-muted-foreground">
                      Employment Details
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-start gap-3">
                      <Calendar className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                      <div className="min-w-0 flex-1">
                        <p className="text-xs text-muted-foreground">Start Date</p>
                        <p className="text-sm font-medium">{formatDate(member.startDate)}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* Working Pattern Tab */}
          <TabsContent value="working-pattern" className="space-y-4 mt-4">
            {isLoading ? (
              <div className="flex items-center justify-center p-12">
                <div className="flex flex-col items-center gap-3">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                  <p className="text-sm text-muted-foreground">Loading working pattern...</p>
                </div>
              </div>
            ) : workingPattern ? (
              <>
                {/* Subtle contextual info instead of alert banner */}
                <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg border">
                  <div className="flex items-center gap-3">
                    <Clock className="h-4 w-4 text-muted-foreground" />
                    <div>
                      <p className="text-sm font-medium">
                        {isInherited ? 'Using Organization Default' : 'Custom Working Pattern'}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {isInherited 
                          ? 'Customize below if this employee needs different hours'
                          : 'This pattern overrides the organization default'
                        }
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {isInherited ? (
                      <Badge variant="secondary" className="text-xs">Default</Badge>
                    ) : (
                      <>
                        <Badge variant="default" className="text-xs">Custom</Badge>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setShowRevertDialog(true)}
                          disabled={isSaving}
                          className="h-8"
                        >
                          <RotateCcw className="h-3.5 w-3.5 mr-1.5" />
                          Revert
                        </Button>
                      </>
                    )}
                  </div>
                </div>

                <WorkingPatternForm
                  workingPattern={workingPattern}
                  onSave={handleSavePattern}
                  readOnly={isSaving}
                  hideConfigurationAlert={true}
                />
              </>
            ) : null}
          </TabsContent>

          {/* Timesheet Tab */}
          <TabsContent value="timesheet" className="space-y-4 mt-4">
            <WorkflowAssignmentCard
              assignment={workflowAssignment}
              isLoading={isLoadingWorkflow}
              onChangeWorkflow={() => setShowWorkflowModal(true)}
              onRevertToDefault={async () => {
                const success = await revertWorkflowToDefault('Reverting to organization default');
                if (success) {
                  toast.success('Reverted to default workflow');
                }
              }}
              employeeName={member.name}
            />
          </TabsContent>
        </Tabs>
      </DialogContent>

      {/* Workflow Selection Modal */}
      <WorkflowSelectionModal
        open={showWorkflowModal}
        onOpenChange={setShowWorkflowModal}
        currentWorkflowId={workflowAssignment?.workflowId}
        employeeName={member.name}
        onSelect={async (workflowId, reason) => {
          const success = await assignWorkflow({ workflowId, reason });
          return success;
        }}
      />

      {/* Revert Confirmation Dialog */}
      <AlertDialog open={showRevertDialog} onOpenChange={setShowRevertDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-warning-500" />
              Revert to Organization Default?
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-3 pt-2">
              <p>
                This will remove {member?.name}'s custom working pattern and restore the organization default pattern.
              </p>
              <Alert>
                <Info className="h-4 w-4" />
                <AlertDescription className="text-xs">
                  This action cannot be undone. The custom pattern will be permanently deleted.
                </AlertDescription>
              </Alert>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isSaving}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setShowRevertDialog(false);
                handleRevertToDefault();
              }}
              disabled={isSaving}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isSaving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Reverting...
                </>
              ) : (
                <>
                  <RotateCcw className="h-4 w-4 mr-2" />
                  Revert to Default
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
};
